/**
 * ViewModeToggle — SegmentedControl for switching between table/card/master-detail views
 *
 * Follows the existing SegmentedControl patterns used across 13+ places in the portal.
 * Visual language matches ViewSwitcher.tsx and TaskListPage toggle.
 */

import { SegmentedControl, Tooltip, Box, type MantineSize } from '@mantine/core';
import { IconTable, IconLayoutGrid, IconColumns, IconLayoutKanban, IconMap, IconClock, IconDeviceDesktop } from '@tabler/icons-react';
import type { DisplayViewMode } from '@/hooks/useViewMode';

const VIEW_MODE_CONFIG: Record<
  DisplayViewMode,
  { icon: React.ComponentType<{ size: number }>; label: string }
> = {
  table: { icon: IconTable as React.ComponentType<{ size: number }>, label: 'Table view' },
  card: { icon: IconLayoutGrid as React.ComponentType<{ size: number }>, label: 'Card view' },
  'master-detail': { icon: IconColumns as React.ComponentType<{ size: number }>, label: 'Master-detail view' },
  kanban: { icon: IconLayoutKanban as React.ComponentType<{ size: number }>, label: 'Kanban board' },
  spatial: { icon: IconMap as React.ComponentType<{ size: number }>, label: 'Spatial view' },
  timeline: { icon: IconClock as React.ComponentType<{ size: number }>, label: 'Timeline view' },
  ambient: { icon: IconDeviceDesktop as React.ComponentType<{ size: number }>, label: 'Ambient display' },
};

interface ViewModeToggleProps {
  value: DisplayViewMode;
  onChange: (mode: DisplayViewMode) => void;
  enabledModes?: DisplayViewMode[];
  size?: MantineSize;
}

export function ViewModeToggle({
  value,
  onChange,
  enabledModes = ['table', 'card'],
  size = 'xs',
}: ViewModeToggleProps) {
  const data = enabledModes.map((mode) => {
    const config = VIEW_MODE_CONFIG[mode];
    const Icon = config.icon;
    return {
      value: mode,
      label: (
        <Tooltip label={config.label} position="bottom" withArrow>
          <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon size={16} />
          </Box>
        </Tooltip>
      ),
    };
  });

  return (
    <SegmentedControl
      value={value}
      onChange={(v) => onChange(v as DisplayViewMode)}
      data={data}
      size={size}
      data-testid="view-mode-toggle"
    />
  );
}
